import torusAlpha from '@/assets/brand/torus-alpha.webp';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { KorzaTorus } from '@/components/brand/korza-torus/korza-torus';
import { LongArrow } from '@/components/brand/long-arrow';
import { APP_CARD_BACKGROUND } from '@/components/home/app-card-palette';
import { useI18nContext } from '@/i18n/i18n-react';
import type { EditionModule } from '@/lib/whats-new/edition-types';
import { cn } from '@/lib/utils';

export interface FeaturedEditionCardProps {
  edition: EditionModule;
  /** `null` while the catalog doesn't say (loading, failed): no pill. */
  seen: boolean | null;
  /** The live torus at the right (≥ 768px only, NEWS-38). */
  showTorus: boolean;
  /** Opens the edition on its first slide. */
  onWatch: () => void;
}

/**
 * The navy card of the newest edition on the page (NEWS-38): name and date,
 * "Nova"/"Vista", title, up to 5 highlights and the button that plays it;
 * on ≥ 768px the live torus whole in its own box at the right, never under
 * the text, unmounted (and destroyed) with the card (NEWS-43).
 */
export function FeaturedEditionCard({
  edition,
  seen,
  showTorus,
  onWatch,
}: FeaturedEditionCardProps) {
  const { LL } = useI18nContext();
  const { featured } = edition;

  return (
    <section
      data-testid="featured-edition"
      className="relative isolate grid min-h-[280px] overflow-hidden rounded-lg text-white shadow-[0_24px_40px_-28px_rgba(11,52,140,0.6)] md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] max-md:min-h-0 max-md:rounded-mobile-card"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-20"
        style={{ background: APP_CARD_BACKGROUND }}
      />
      <GrainOverlay className="-z-10" />

      <div
        data-testid="featured-copy"
        className="flex min-w-0 flex-col justify-center gap-4 p-8 max-md:p-[22px]"
      >
        <div className="flex flex-wrap items-center gap-2.5 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-white/[.86]">
          <span>{featured.eyebrow(LL)}</span>
          {seen !== null && (
            <span
              data-testid="edition-pill"
              className={cn(
                'inline-flex h-[22px] items-center rounded-full px-[9px] tracking-[0.06em]',
                seen ? 'bg-white/[.14] text-white' : 'bg-white text-[#15191E]'
              )}
            >
              {seen ? LL.whatsNew.page.seen() : LL.whatsNew.page.new()}
            </span>
          )}
        </div>

        <h2 className="text-balance text-[32px] font-medium leading-[1.08] tracking-[-0.03em] max-md:text-[26px]">
          {featured.title(LL)}
        </h2>

        <ul className="flex flex-col gap-[7px] text-[13.5px] text-white/[.86]">
          {edition.highlights.slice(0, 5).map((highlight) => (
            <li
              key={highlight.label(LL) + highlight.text(LL)}
              data-highlight
              className="flex gap-2.5 max-md:flex-col max-md:gap-px"
            >
              <b
                data-highlight-label
                className="min-w-[78px] font-semibold text-white"
              >
                {highlight.label(LL)}
              </b>
              <span data-highlight-text>{highlight.text(LL)}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={onWatch}
          className="group inline-flex h-[42px] items-center gap-3 self-start rounded-full bg-white px-[18px] text-sm font-semibold text-[#15191E] outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B348C] max-md:min-h-touch"
        >
          {seen ? featured.rewatch(LL) : featured.watch(LL)}
          <LongArrow lineClassName="w-7 group-hover:w-11" />
        </button>
      </div>

      {showTorus && (
        <div
          data-testid="featured-torus"
          aria-hidden="true"
          className="relative min-h-[240px]"
        >
          <KorzaTorus
            zoom={0.8}
            className="absolute inset-0 size-full"
            fallback={
              <img
                src={torusAlpha}
                alt=""
                className="absolute inset-0 size-full object-contain p-6"
              />
            }
          />
        </div>
      )}
    </section>
  );
}
